import { logger } from '@/utils/logger';
import useCategories from '@/hooks/use-categories';
import appServices from '@/services/appServices';
import { AppCategory, getRandomCategoryColor } from '@polybank/shared';
import { extractKeywordFromConditions } from '@polybank/shared/rule-utils';
import { AIRuleResponse } from '@polybank/shared/types/ai-contracts';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { handleApiError } from '@/lib/api-error-handler';

export interface GenerateRuleParams {
  transactionDescription: string;
  transactionAmount: number;
  customPrompt?: string;
  /** Optional category name to pass to AI for targeted rule generation */
  categoryName?: string;
}

/**
 * General-purpose AI hook for various AI operations
 * Provides methods for rule generation and other AI features
 */
export function useAi() {
  // const [isLoading, setIsLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState<AIRuleResponse>();
  const [isRegenerating, setIsRegenerating] = useState(false);
  const { categoriesMap } = useCategories();

  const categoriesMapByName = useMemo(() => {
    return Object.values(categoriesMap).reduce(
      (acc, category) => {
        acc[category.name] = category;
        return acc;
      },
      {} as Record<string, AppCategory>
    );
  }, [categoriesMap]);

  const generateRuleMutation = useMutation({
    mutationFn: async ({
      transactionDescription,
      transactionAmount,
      customPrompt,
      categoryName,
    }: GenerateRuleParams) => {
      setIsRegenerating(!!customPrompt);

      setAiResponse(undefined);

      try {
        const result = await appServices.ai.generateRule({
          description: transactionDescription,
          amount: transactionAmount,
          date: new Date().toISOString().split('T')[0],
          customPrompt,
          categoryName,
        });

        if (result?.data) {
          const response = result.data as unknown as AIRuleResponse;
          setAiResponse(response);
          return response;
        } else {
          throw new Error('Invalid response from AI service');
        }
      } catch (error) {
        logger.error('Failed to call AI service:', error);
        handleApiError(error, 'Failed to generate rule');
        return undefined;
      } finally {
        setIsRegenerating(false);
      }
    },
  });

  /**
   * Reset all AI state
   */
  const reset = useCallback(() => {
    setAiResponse(undefined);
    generateRuleMutation.reset();
    setIsRegenerating(false);
  }, [generateRuleMutation]);

  const aiSelectedCategory = useMemo(() => {
    const setCategoryAction = aiResponse?.conditions.result.value.find(
      (action) => action.actionType === 'set_category'
    );

    if (setCategoryAction) {
      const categoryName = setCategoryAction.actionValue.toString();
      const existingCategory = categoriesMapByName[categoryName];
      if (existingCategory) {
        return existingCategory;
      }
      const randomColor = getRandomCategoryColor();
      return {
        name: categoryName,
        color: randomColor.bg,
        text_color: randomColor.text,
      } satisfies AppCategory;
    }
  }, [aiResponse, categoriesMapByName]);

  const isNewCategory = useMemo(() => {
    return aiSelectedCategory?.$id === undefined;
  }, [aiSelectedCategory]);

  const humanReadable = useMemo(() => {
    return aiResponse?.humanReadable;
  }, [aiResponse]);

  const conditions = useMemo(() => {
    if (aiResponse?.conditions && 'and' in aiResponse.conditions) {
      return aiResponse.conditions.and ?? [];
    } else if (aiResponse?.conditions && 'or' in aiResponse.conditions) {
      return aiResponse.conditions.or ?? [];
    }
    return [];
  }, [aiResponse]);

  const actions = useMemo(() => {
    return aiResponse?.conditions.result?.value ?? [];
  }, [aiResponse]);

  const aiSelectedKeyword = useMemo(() => {
    return extractKeywordFromConditions(conditions);
  }, [conditions]);

  const aiPatternType = useMemo(() => {
    const firstAction = aiResponse?.conditions?.result?.value?.[0];
    return firstAction?.patternType ?? 'exact';
  }, [aiResponse]);

  return {
    // State
    aiResponse,
    isLoading: generateRuleMutation.isPending,
    isNewCategory,
    aiSelectedCategory,
    aiSelectedKeyword,
    aiPatternType,
    isRegenerating,
    humanReadable,
    conditions,
    actions,
    reasoning: aiResponse?.reasoning,
    // Methods
    generateRule: generateRuleMutation.mutateAsync,
    reset,
    // buildRawRule,
  };
}
